import { z } from "zod";

// ── Task context ─────────────────────────────────────────────

export const TaskContextSchema = z.object({
  description: z.string(),
  taskType: z.string().optional(),
  complexity: z.enum(["S", "M", "L", "XL"]),
  category: z.string().optional(),
});

export interface TaskContext {
  description: string;
  taskType?: string; // scaffold, api-endpoint, react-component, debugging, etc.
  complexity: "S" | "M" | "L" | "XL";
  category?: string; // infrastructure, frontend, backend, etc.
}

// ── Budget state ─────────────────────────────────────────────

export interface BudgetState {
  monthlyLimit: number;
  currentSpend: number;
  daysRemaining: number;
  currency: string;
}

// ── Calibration ──────────────────────────────────────────────

export interface CalibrationEntry {
  model: string;
  taskType: string;
  complexity: string;
  estimatedTokens: number;
  actualTokens: number;
  accuracy: number; // actual/estimated ratio
  timestamp: number;
}

export interface CalibrationHistory {
  entries: CalibrationEntry[];
}

// ── Model info ───────────────────────────────────────────────

export interface ModelInfo {
  id: string; // e.g. "claude-sonnet-4-20250514"
  name: string; // human-readable name
  provider: string; // "anthropic", "openai", "google"
  tier: "frontier" | "mid" | "economy";
  inputPerMTok: number;
  outputPerMTok: number;
  cacheReadPerMTok?: number;
  batchDiscount?: number;
  capabilities: string[]; // ["code", "reasoning", "creative", "fast"]
  deprecated?: boolean; // true if model is no longer recommended
  successor?: string; // model ID of recommended replacement
}

// ── Recommendation ───────────────────────────────────────────

export interface Recommendation {
  model: string;
  provider: string;
  tier: string;
  reason: string;
  estimatedCost: number;
  confidence: "high" | "medium" | "low";
  dataPoints: number;
  alternatives: Array<{
    model: string;
    reason: string;
    estimatedCost: number;
  }>;
  guardrailsToInject: string[];
  budgetWarning?: string;
  deprecationWarning?: string;
}

// ── Adjustment report ────────────────────────────────────────

export interface AdjustmentReport {
  adjustments: Array<{
    taskType: string;
    complexity: string;
    currentModel: string;
    suggestedModel: string;
    reason: string;
    confidence: number;
    dataPoints: number;
  }>;
  summary: string;
}

// ── Router config ────────────────────────────────────────────

export interface RouterConfig {
  intent?: {
    budget?: { monthly_limit: number; currency?: string };
    guardrails?: { security?: { templates?: string[] }; quality?: string[] };
    providers?: { preferred?: string };
  };
  calibration?: CalibrationHistory;
  models?: ModelInfo[];
}
